import { join } from 'node:path';
import { deepScanWorkspace } from '../../../src/station/fileScanner';
import {
  createStationOrchestrator,
  type StationRunResult
} from '../../../src/station/orchestrator';
import type { ScanResult } from '../../../src/station/types';
import type { ProductionConnectionChoices } from '../../../src/station/productionConnections';
import { hydrateArtifactForReport } from './report/hydrateArtifact';
import type { CliScanArtifact, StationRunSuccess } from './types';
import { loadStackChoicesFile } from './config';
import { buildLocalStationOutput } from './localStation';
import { buildArchitectureData, buildArchitectureMap } from './architectureMap';

function computeProductionCorePercent(missionGraph: StationRunResult['missionGraph']): number {
  const areas = missionGraph.areas ?? [];
  if (areas.length === 0) {
    return 0;
  }
  const sum = areas.reduce((acc, area) => acc + (area.readinessPercent ?? 0), 0);
  return Math.round(sum / areas.length);
}

function toArtifact(
  workspacePath: string,
  result: StationRunSuccess,
  selectedProviders: Record<string, string>
): CliScanArtifact {
  const artifact: CliScanArtifact = {
    version: 1,
    scannedAt: new Date().toISOString(),
    workspacePath,
    score: result.score,
    scoreLabel: result.scoreLabel,
    summary: result.summary,
    archetype: result.archetype,
    gaps: result.gaps,
    missionGraph: result.missionGraph,
    stackWiring: result.stackWiring,
    stackAutomation: result.stackAutomation,
    providerRegistry: result.providerRegistry,
    verificationSummary: result.verificationSummary,
    productionCorePercent: computeProductionCorePercent(result.missionGraph),
    selectedProviders
  };

  if (result.providerTruth) {
    artifact.providerTruth = result.providerTruth;
  }
  if (result.launchValidation) {
    artifact.launchValidation = result.launchValidation;
  }

  return artifact;
}

async function loadProductionChoices(workspacePath: string): Promise<ProductionConnectionChoices | undefined> {
  const file = await loadStackChoicesFile(workspacePath);
  if (Object.keys(file.choices).length === 0) {
    return undefined;
  }
  // The orchestrator normalizes this shape via `normalizeProductionChoice`.
  return file as unknown as ProductionConnectionChoices;
}

export type RunScanOptions = {
  workspacePath?: string;
};

export type RunScanResult =
  | { ok: true; artifact: CliScanArtifact }
  | { ok: false; kind: 'error'; message: string };

/**
 * Runs the full scan pipeline locally and offline.
 * All evidence comes from the repo on disk; the gap engine is
 * deterministic (`buildLocalStationOutput`). No login, no network.
 */
export async function runProjectScan(options: RunScanOptions = {}): Promise<RunScanResult> {
  const workspacePath = options.workspacePath ?? process.cwd();
  let lastScan: ScanResult | undefined;

  const orchestrator = createStationOrchestrator({
    scanWorkspace: async (root) => {
      lastScan = await deepScanWorkspace(root);
      return lastScan;
    },
    getProductionConnectionChoices: () => loadProductionChoices(workspacePath),
    isLocalStationFallbackAllowed: () => true,
    fetchStationOutput: async () => {
      if (!lastScan) {
        throw new Error('Local scan evidence missing.');
      }
      return buildLocalStationOutput({ scan: lastScan, workspacePath });
    }
  });

  try {
    const result = await orchestrator.run({
      workspaceRoot: workspacePath,
      prompt: 'Full local station scan for CLI launch report',
      configuration: undefined
    });

    const stackFile = await loadStackChoicesFile(workspacePath);
    const selectedProviders: Record<string, string> = {};
    for (const [area, choice] of Object.entries(stackFile.choices)) {
      if (choice && typeof choice.provider === 'string') {
        selectedProviders[area] = choice.provider;
      }
    }
    const artifact = hydrateArtifactForReport(
      toArtifact(workspacePath, result as StationRunSuccess, selectedProviders)
    );
    if (lastScan) {
      const archInput = {
        scan: lastScan,
        archetype: artifact.archetype,
        stackDetected: (result as StationRunSuccess).stackDetected ?? [],
      };
      artifact.architectureMap = buildArchitectureMap(archInput);
      artifact.architectureData = buildArchitectureData(archInput);
    }
    return { ok: true, artifact };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return { ok: false, kind: 'error', message };
  }
}

export function defaultPromptPath(workspacePath: string): string {
  return join(workspacePath, '.viberaven', 'last-scan.json');
}
